import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Hotel, LayoutDashboard, Bed, Users, Calendar, UtensilsCrossed, 
  Menu as MenuIcon, Briefcase, FileText, Receipt, Settings, 
  X, Mail, ClipboardList, ExternalLink, History, DoorOpen,
  Database, ShoppingCart, UserCog, Sparkles, CreditCard,
  Palette, Package, Ticket, Store, BarChart3, Wallet, BadgeDollarSign,
  MessageCircle, ConciergeBell, ChefHat, Calculator, BookOpen, PieChart,
  Building2, FileCheck, Cog, MessageSquare, Shield, TrendingUp, Target, Lock,
  ChevronDown, ChevronRight, Cpu, Globe, Phone, Server, Wifi, Cloud, Layers
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import HotelSwitcher from './HotelSwitcher';
import CompanySelector from './CompanySelector';
import TopHeader from './TopHeader';
import TabsContainer from './TabsContainer';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';
import SIPDialer from './SIPDialer';
import MizuDialer from './MizuDialer';

// Module to navigation group mapping
const MODULE_TO_NAV_GROUPS = {
  purchase: ['purchase'],
  finance: ['finance'],
  assets: ['assets'],
  inventory: ['inventory'],
  budget: ['budget'],
  portal: ['portal']
};

// Navigation grouped by category
// Role Hierarchy:
// Super Admin -> Company Admin -> Hotel Admin -> Manager/Front Office/Service Manager/Restaurant Staff
const navigationGroups = {
  admin: {
    title: 'Admin',
    icon: UserCog,
    items: [
      { name: 'Dashboard', href: '/admin', icon: LayoutDashboard, roles: ['super_admin', 'company_admin', 'hotel_admin', 'finance_admin', 'manager', 'front_office', 'service_manager', 'restaurant_staff'] },
      // Super Admin Only - Company Management (Hotels managed under Companies)
      { name: 'Companies', href: '/admin/companies', icon: Building2, roles: ['super_admin'] },
      { name: 'All Users', href: '/admin/super-admin/users', icon: Users, roles: ['super_admin'] },
      { name: 'Live Sessions', href: '/admin/live-sessions', icon: Wifi, roles: ['super_admin', 'company_admin', 'hotel_admin'] },
      { name: 'Device Integration', href: '/admin/super-admin/kiot', icon: Cpu, roles: ['super_admin'] },
      // Company Admin - Hotel & Finance Management
      { name: 'Hotels', href: '/admin/super-admin/dashboard', icon: Hotel, roles: ['company_admin'] },
      { name: 'Finance Setup', href: '/admin/company/finance-setup', icon: Calculator, roles: ['super_admin', 'company_admin', 'finance_admin'], requiredModule: 'finance_setup' },
      { name: 'Company Users', href: '/admin/company/users', icon: Users, roles: ['company_admin'] },
      { name: 'Employees', href: '/admin/company/employees', icon: Users, roles: ['company_admin'] },
      // Hotel Admin - User Management
      { name: 'Users', href: '/admin/users', icon: Users, roles: ['hotel_admin', 'manager'] },
      { name: 'Employees', href: '/admin/employees', icon: Users, roles: ['hotel_admin'] },
      { name: 'Roles', href: '/admin/settings/roles', icon: Settings, roles: ['super_admin', 'company_admin', 'hotel_admin', 'manager'] },
      // Settings
      { name: 'Theme Settings', href: '/admin/settings/theme', icon: Palette, roles: ['super_admin', 'company_admin', 'hotel_admin'] },
      { name: 'Payment Gateway', href: '/admin/settings/payment', icon: Wallet, roles: ['super_admin', 'company_admin', 'hotel_admin'], requiredModule: 'payment_gateway' },
      { name: 'Communication', href: '/admin/settings/communication', icon: MessageSquare, roles: ['super_admin', 'company_admin', 'hotel_admin'], requiredModule: 'communication' },
      { name: 'Cloud Storage (R2)', href: '/admin/storage-settings', icon: Cloud, roles: ['super_admin'] },
      { name: 'POS Settings', href: '/admin/settings/pos', icon: CreditCard, roles: ['hotel_admin', 'manager'] },
      { name: 'KDS Settings', href: '/admin/settings/kds', icon: ChefHat, roles: ['hotel_admin', 'manager'] },
      { name: 'Lock Settings', href: '/admin/settings/locks', icon: Lock, roles: ['hotel_admin', 'manager'] },
      { name: 'PBX / Phone', href: '/admin/settings/phone', icon: Server, roles: ['hotel_admin', 'manager'] },
      { name: 'PBX Management', href: '/admin/pbx-management', icon: Server, roles: ['super_admin'] },
      { name: 'Audit Trail', href: '/admin/audit-trail', icon: Shield, roles: ['super_admin'] },
    ]
  },
  masterData: {
    title: 'Master Data',
    icon: Database,
    items: [
      { name: 'Rooms', href: '/admin/rooms', icon: Bed, roles: ['super_admin', 'hotel_admin', 'manager', 'front_office'] },
      { name: 'Restaurants', href: '/admin/restaurants', icon: Store, roles: ['hotel_admin', 'manager', 'restaurant_staff'] },
      { name: 'Services', href: '/admin/services', icon: Briefcase, roles: ['hotel_admin', 'manager', 'front_office', 'service_manager'] },
      { name: 'Amenities', href: '/admin/amenities-master', icon: Layers, roles: ['super_admin'] },
      { name: 'Room Amenities', href: '/admin/room-amenities', icon: Sparkles, roles: ['hotel_admin', 'manager'] },
    ]
  },
  assets: {
    title: 'Assets',
    icon: Package,
    items: [
      { name: 'All Assets', href: '/admin/assets', icon: Package, roles: ['company_admin', 'hotel_admin', 'manager', 'asset_manager'] },
      { name: 'Asset Categories', href: '/admin/assets?tab=categories', icon: Database, roles: ['company_admin', 'hotel_admin', 'manager'] },
      { name: 'Maintenance', href: '/admin/assets?tab=maintenance', icon: Settings, roles: ['company_admin', 'hotel_admin', 'manager', 'asset_manager'] },
    ]
  },
  purchase: {
    title: 'Purchase',
    icon: ShoppingCart,
    items: [
      { name: 'Vendors', href: '/admin/vendors', icon: Building2, roles: ['company_admin', 'hotel_admin', 'manager', 'purchase_manager'] },
      { name: 'Requisitions', href: '/admin/purchase-requisitions', icon: FileText, roles: ['company_admin', 'hotel_admin', 'manager', 'purchase_manager', 'store_manager'] },
      { name: 'Purchase Orders', href: '/admin/purchase-orders', icon: ClipboardList, roles: ['company_admin', 'hotel_admin', 'manager', 'purchase_manager'] },
      { name: 'Goods Receipt', href: '/admin/grn', icon: FileCheck, roles: ['company_admin', 'hotel_admin', 'manager', 'purchase_manager', 'store_manager'] },
      { name: 'Purchase Invoices', href: '/admin/purchase-invoices', icon: Receipt, roles: ['company_admin', 'hotel_admin', 'finance_admin', 'manager', 'purchase_manager'] },
    ]
  },
  inventory: {
    title: 'Inventory',
    icon: Store,
    items: [
      { name: 'Stores', href: '/admin/stores', icon: Store, roles: ['company_admin', 'hotel_admin', 'manager', 'store_manager'] },
      { name: 'Item Master', href: '/admin/item-master', icon: Package, roles: ['company_admin', 'hotel_admin', 'manager', 'store_manager', 'purchase_manager'] },
      { name: 'Stock Movements', href: '/admin/stock-movements', icon: History, roles: ['company_admin', 'hotel_admin', 'manager', 'store_manager'] },
    ]
  },
  budget: {
    title: 'Budget & Planning',
    icon: Target,
    items: [
      { name: 'Budget Dashboard', href: '/admin/budget', icon: TrendingUp, roles: ['company_admin', 'hotel_admin', 'finance_admin', 'manager'] },
      { name: 'Budget Setup', href: '/admin/budget/setup', icon: Settings, roles: ['company_admin', 'finance_admin'] },
      { name: 'Hotel Allocations', href: '/admin/budget/allocations', icon: Building2, roles: ['company_admin', 'finance_admin'] },
    ]
  },
  portal: {
    title: 'Online Portal',
    icon: Globe,
    items: [
      { name: 'Portal Settings', href: '/admin/portal/settings', icon: Settings, roles: ['super_admin'] },
      { name: 'Property Registrations', href: '/admin/portal/registrations', icon: FileText, roles: ['super_admin'] },
      { name: 'Property Listing', href: '/admin/portal/listing', icon: Building2, roles: ['super_admin', 'company_admin', 'hotel_admin'] },
      { name: 'Portal Users', href: '/admin/portal/users', icon: Users, roles: ['super_admin', 'company_admin'] },
    ]
  },
  transactional: {
    title: 'Transactional',
    icon: ShoppingCart,
    items: [
      { name: 'Guests', href: '/admin/guests', icon: Users, roles: ['hotel_admin', 'manager', 'front_office'] },
      { name: 'Bookings', href: '/admin/bookings', icon: Calendar, roles: ['hotel_admin', 'manager', 'front_office'] },
      { name: 'Restaurant POS', href: '/admin/restaurant', icon: UtensilsCrossed, roles: ['hotel_admin', 'manager', 'restaurant_staff', 'service_manager'], popup: true },
      { name: 'Table Order', href: '/admin/table-order', icon: ConciergeBell, roles: ['hotel_admin', 'manager', 'restaurant_staff', 'service_manager', 'front_office'], popup: true },
      { name: 'Kitchen Display', href: '/admin/kds', icon: ChefHat, roles: ['hotel_admin', 'manager', 'restaurant_staff', 'service_manager'], popup: true },
      { name: 'Order History', href: '/admin/order-history', icon: History, roles: ['hotel_admin', 'manager', 'restaurant_staff', 'service_manager', 'front_office'] },
      { name: 'Service Orders', href: '/admin/service-orders', icon: ClipboardList, roles: ['hotel_admin', 'manager', 'front_office', 'service_manager'] },
      { name: 'Orders Dashboard', href: '/admin/orders-dashboard', icon: ClipboardList, roles: ['hotel_admin', 'manager'] },
      { name: 'Staff Dashboard', href: '/admin/staff-dashboard', icon: ConciergeBell, roles: ['service_staff'] },
      { name: 'Housekeeping', href: '/admin/housekeeping-dashboard', icon: Sparkles, roles: ['house_keeping'] },
      { name: 'Housekeeping Mgmt', href: '/admin/housekeeping', icon: Sparkles, roles: ['hotel_admin', 'manager', 'front_office'] },
      { name: 'Credit Accounts', href: '/admin/credit-accounts', icon: BadgeDollarSign, roles: ['hotel_admin', 'manager', 'front_office'] },
      { name: 'Support Tickets', href: '/admin/tickets', icon: Ticket, roles: ['hotel_admin', 'manager', 'front_office', 'restaurant_staff', 'service_manager'] },
      { name: 'Reports', href: '/admin/reports', icon: BarChart3, roles: ['hotel_admin', 'manager'] },
    ]
  },
  finance: {
    title: 'Finance & Accounting',
    icon: Calculator,
    items: [
      { name: 'Finance Dashboard', href: '/admin/finance', icon: PieChart, roles: ['company_admin', 'finance_admin', 'hotel_admin', 'manager'] },
      { name: 'Pending Postings', href: '/admin/finance/postings', icon: FileCheck, roles: ['company_admin', 'finance_admin'] },
      { name: 'Ledgers', href: '/admin/finance/ledgers', icon: BookOpen, roles: ['company_admin', 'finance_admin', 'hotel_admin', 'manager'] },
      { name: 'Journal Entry', href: '/admin/finance/journal', icon: FileText, roles: ['company_admin', 'finance_admin', 'hotel_admin', 'manager'] },
      { name: 'Financial Reports', href: '/admin/finance/reports', icon: BarChart3, roles: ['company_admin', 'finance_admin', 'hotel_admin', 'manager'] },
      { name: 'GST Reports', href: '/admin/finance/gst', icon: Receipt, roles: ['company_admin', 'finance_admin', 'hotel_admin', 'manager'] },
    ]
  }
};

export default function Layout({ children, user, onLogout, activeHotel, onHotelChange, selectedCompany, onCompanyChange, selectedFinancialYear, onFinancialYearChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openTab } = useTabs();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentHotel, setCurrentHotel] = useState(null);
  const [enabledModules, setEnabledModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({
    admin: true,
    masterData: false,
    assets: false,
    purchase: false,
    inventory: false,
    budget: false,
    transactional: true,
    finance: false
  });

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Fetch hotel details for hotel_admin users
  useEffect(() => {
    const fetchHotelDetails = async () => {
      if (user.role === 'hotel_admin' && user.hotel_id) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/my-hotel`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const hotelData = await response.json();
            setCurrentHotel(hotelData);
          }
        } catch (error) {
          console.error('Failed to fetch hotel details:', error);
        }
      }
    };
    fetchHotelDetails();
  }, [user.role, user.hotel_id]);

  // Fetch company's enabled modules for non-super_admin users
  useEffect(() => {
    const fetchCompanyModules = async () => {
      // Super Admin sees all modules
      if (user.role === 'super_admin') {
        setEnabledModules(['purchase', 'finance', 'assets', 'inventory', 'budget']);
        setLoadingModules(false);
        return;
      }
      
      // Get company_id from user, selectedCompany, or currentHotel (for hotel_admin)
      const companyId = user.company_id || selectedCompany?.id || currentHotel?.company_id;
      if (!companyId) {
        // If no company_id yet, wait for hotel data to load (for hotel_admin)
        if (user.role === 'hotel_admin' && user.hotel_id && !currentHotel) {
          // Still loading hotel data, don't set empty modules yet
          return;
        }
        setEnabledModules([]);
        setLoadingModules(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/modules`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setEnabledModules(data.enabled_modules || []);
        } else {
          setEnabledModules([]);
        }
      } catch (error) {
        console.error('Failed to fetch company modules:', error);
        setEnabledModules([]);
      } finally {
        setLoadingModules(false);
      }
    };
    
    fetchCompanyModules();
  }, [user.role, user.company_id, user.hotel_id, selectedCompany?.id, currentHotel]);

  const openPopupWindow = (url, title) => {
    const width = 1400;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;
    
    window.open(
      url,
      title,
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`
    );
  };

  // Map URLs to tab types
  const HREF_TO_TAB_TYPE = {
    // Dashboard
    '/admin': TAB_TYPES.PAGE_DASHBOARD,
    // Admin
    '/admin/companies': TAB_TYPES.PAGE_COMPANIES,
    '/admin/super-admin/users': TAB_TYPES.PAGE_SUPER_ADMIN_USERS,
    '/admin/super-admin/dashboard': TAB_TYPES.PAGE_HOTELS,
    '/admin/company/finance-setup': TAB_TYPES.PAGE_FINANCE_SETUP,
    '/admin/company/users': TAB_TYPES.PAGE_COMPANY_USERS,
    '/admin/users': TAB_TYPES.PAGE_USERS,
    '/admin/settings/roles': TAB_TYPES.PAGE_ROLES,
    '/admin/settings/theme': TAB_TYPES.PAGE_THEME_SETTINGS,
    '/admin/settings/payment': TAB_TYPES.PAGE_PAYMENT_SETTINGS,
    '/admin/settings/communication': TAB_TYPES.PAGE_COMMUNICATION_SETTINGS,
    '/admin/settings/pos': TAB_TYPES.PAGE_POS_SETTINGS,
    '/admin/settings/kds': TAB_TYPES.PAGE_KDS_SETTINGS,
    // Master Data
    '/admin/rooms': TAB_TYPES.PAGE_ROOMS,
    '/admin/restaurants': TAB_TYPES.PAGE_RESTAURANTS,
    '/admin/services': TAB_TYPES.PAGE_SERVICES,
    '/admin/id-types': TAB_TYPES.PAGE_ID_TYPES,
    // Assets
    '/admin/assets': TAB_TYPES.PAGE_ASSETS,
    // Purchase
    '/admin/vendors': TAB_TYPES.PAGE_VENDORS,
    '/admin/purchase-requisitions': TAB_TYPES.PAGE_REQUISITIONS,
    '/admin/purchase-orders': TAB_TYPES.PAGE_PURCHASE_ORDERS,
    '/admin/grn': TAB_TYPES.PAGE_GRN,
    '/admin/purchase-invoices': TAB_TYPES.PAGE_PURCHASE_INVOICES,
    // Inventory
    '/admin/stores': TAB_TYPES.PAGE_STORES,
    '/admin/item-master': TAB_TYPES.PAGE_ITEM_MASTER,
    '/admin/stock-movements': TAB_TYPES.PAGE_STOCK_MOVEMENTS,
    // Budget
    '/admin/budget': TAB_TYPES.PAGE_BUDGET,
    '/admin/budget/setup': TAB_TYPES.PAGE_BUDGET_SETUP,
    '/admin/budget/allocations': TAB_TYPES.PAGE_BUDGET_ALLOCATIONS,
    // Transactional
    '/admin/guests': TAB_TYPES.PAGE_GUESTS,
    '/admin/bookings': TAB_TYPES.PAGE_BOOKINGS,
    '/admin/order-history': TAB_TYPES.PAGE_ORDER_HISTORY,
    '/admin/service-orders': TAB_TYPES.PAGE_SERVICE_ORDERS,
    '/admin/credit-accounts': TAB_TYPES.PAGE_CREDIT_ACCOUNTS,
    '/admin/tickets': TAB_TYPES.PAGE_SUPPORT_TICKETS,
    '/admin/reports': TAB_TYPES.PAGE_REPORTS,
    // Finance
    '/admin/finance': TAB_TYPES.PAGE_FINANCE_DASHBOARD,
    '/admin/finance/postings': TAB_TYPES.PAGE_PENDING_POSTINGS,
    '/admin/finance/ledgers': TAB_TYPES.PAGE_LEDGERS,
    '/admin/finance/journal': TAB_TYPES.PAGE_JOURNAL_ENTRIES,
    '/admin/finance/reports': TAB_TYPES.PAGE_FINANCIAL_REPORTS,
    '/admin/finance/gst': TAB_TYPES.PAGE_GST_REPORTS,
    // Settings
    '/admin/settings/locks': TAB_TYPES.PAGE_LOCK_SETTINGS,
    '/admin/settings/phone': TAB_TYPES.PAGE_PHONE_SETTINGS,
    '/admin/pbx-management': TAB_TYPES.PAGE_PBX_MANAGEMENT,
    '/admin/audit-trail': TAB_TYPES.PAGE_AUDIT_TRAIL,
    '/admin/live-sessions': TAB_TYPES.PAGE_LIVE_SESSIONS,
    // Online Portal
    '/admin/portal/settings': TAB_TYPES.PAGE_PORTAL_SETTINGS,
    '/admin/portal/registrations': TAB_TYPES.PAGE_PROPERTY_REGISTRATIONS,
    '/admin/portal/listing': TAB_TYPES.PAGE_PROPERTY_LISTING,
    '/admin/portal/users': TAB_TYPES.PAGE_PORTAL_USERS,
    // Cloud Storage
    '/admin/storage-settings': TAB_TYPES.PAGE_CLOUD_STORAGE,
  };

  const handleNavClick = (item) => {
    if (item.popup) {
      // Open the correct popup based on the item's href
      if (item.href === '/admin/restaurant') {
        openPopupWindow('/admin/restaurant?popup=true', 'BIZIONIX HMS POS');
      } else if (item.href === '/admin/kds') {
        openPopupWindow('/admin/kds', 'Kitchen Display System');
      } else if (item.href === '/admin/table-order') {
        openPopupWindow('/admin/table-order?popup=true', 'Table Order');
      } else {
        openPopupWindow(item.href, item.name);
      }
    } else {
      // Open as tab instead of navigating
      const tabType = HREF_TO_TAB_TYPE[item.href];
      if (tabType) {
        openTab({
          type: tabType,
          id: item.href.replace(/\//g, '-') || 'home',
          title: item.name,
          data: { href: item.href }
        });
      } else {
        // Fallback to navigation for unsupported pages
        navigate(item.href);
      }
    }
    setMobileMenuOpen(false);
  };

  const renderNavGroup = (groupKey, group) => {
    // Check if this navigation group requires a module that is not enabled
    // Super Admin always sees all modules
    if (user.role !== 'super_admin') {
      // Find which module this nav group belongs to
      for (const [moduleId, navGroups] of Object.entries(MODULE_TO_NAV_GROUPS)) {
        if (navGroups.includes(groupKey)) {
          // This nav group requires this module - check if it's enabled
          if (!enabledModules.includes(moduleId)) {
            return null; // Module not enabled, hide entire nav group
          }
        }
      }
    }
    
    const filteredItems = group.items.filter(item => {
      // Check if user has any of the required roles
      const userRoles = user.roles || [user.role];
      const hasRole = item.roles.some(r => userRoles.includes(r));
      if (!hasRole) return false;
      
      // Check if item requires a specific module (for items inside admin group that belong to a module)
      // Super Admin always sees all items
      if (user.role !== 'super_admin' && item.requiredModule) {
        if (!enabledModules.includes(item.requiredModule)) {
          return false; // Required module not enabled
        }
      }
      
      return true;
    });
    if (filteredItems.length === 0) return null;

    const GroupIcon = group.icon;
    const currentPath = location.pathname;
    const isExpanded = expandedGroups[groupKey];
    
    // Check if any item in this group is active
    const hasActiveItem = filteredItems.some(item => {
      if (item.href === '/admin') {
        return currentPath === '/admin' || currentPath === '/admin/';
      }
      return currentPath === item.href || currentPath.startsWith(item.href + '/');
    });

    return (
      <div key={groupKey} className="mb-1">
        {/* Collapsible Group Header */}
        <button
          onClick={() => toggleGroup(groupKey)}
          className={`
            w-full flex items-center justify-between px-3 py-1.5 rounded-md text-[11px] font-semibold uppercase tracking-wider
            transition-all duration-150 cursor-pointer
            ${hasActiveItem 
              ? 'text-orange-600 bg-orange-50/80' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }
          `}
        >
          <div className="flex items-center gap-2">
            <GroupIcon className="h-3.5 w-3.5" strokeWidth={2} />
            <span>{group.title}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-3 w-3 transition-transform duration-200" />
          ) : (
            <ChevronRight className="h-3 w-3 transition-transform duration-200" />
          )}
        </button>
        
        {/* Collapsible Items Container */}
        <div 
          className={`
            overflow-hidden transition-all duration-200 ease-in-out
            ${isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}
          `}
        >
          <div className="mt-0.5 ml-2 pl-2 border-l border-gray-200/80 space-y-0.5">
            {filteredItems.map((item) => {
              const Icon = item.icon;
              // Fix: Precise active state detection using current pathname
              let isActive = false;
              
              if (item.href === '/admin') {
                // Dashboard is only active if we're exactly at /admin
                isActive = currentPath === '/admin' || currentPath === '/admin/';
              } else {
                // For other paths, match exact path or child paths
                isActive = currentPath === item.href || 
                          currentPath.startsWith(item.href + '/');
              }
              
              return (
                <button
                  key={item.href}
                  onClick={() => handleNavClick(item)}
                  data-testid={`nav-${item.href.slice(1).replace(/\//g, '-') || 'dashboard'}`}
                  className={`
                    w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md font-medium text-xs
                    transition-all duration-150
                    ${isActive 
                      ? 'nav-active shadow-sm' 
                      : 'nav-inactive hover:bg-gray-50'
                    }
                  `}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                    <span className="truncate">{item.name}</span>
                  </div>
                  {item.popup && (
                    <ExternalLink className="h-2.5 w-2.5 opacity-40 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-lg z-50 px-4 py-3 flex items-center justify-between border-b border-orange-100">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-orange flex items-center justify-center shadow-lg">
            <Hotel className="h-5 w-5 text-white" />
          </div>
          <div>
            <span className="font-bold text-lg text-gradient-warm" style={{ fontFamily: 'Manrope' }}>BIZIONIX HMS</span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
          className="text-gray-600 hover:text-orange-600 hover:bg-orange-50"
        >
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-40 h-screen w-60 bg-white border-r border-gray-200 shadow-sm
        transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        <div className="h-full flex flex-col">
          {/* Logo - Show Hotel/Company Name */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-lg bg-gradient-orange flex items-center justify-center shadow-md">
                <Hotel className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-sm font-bold tracking-tight text-gray-800 truncate" style={{ fontFamily: 'Manrope' }}>
                  {selectedCompany?.name || activeHotel?.name || currentHotel?.name || 'BIZIONIX HMS'}
                </h1>
                <p className="text-[10px] text-purple-600 uppercase tracking-wider font-semibold">{user.role.replace('_', ' ')}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-2 overflow-y-auto">
            {renderNavGroup('admin', navigationGroups.admin)}
            {renderNavGroup('masterData', navigationGroups.masterData)}
            {renderNavGroup('assets', navigationGroups.assets)}
            {renderNavGroup('purchase', navigationGroups.purchase)}
            {renderNavGroup('inventory', navigationGroups.inventory)}
            {renderNavGroup('budget', navigationGroups.budget)}
            {renderNavGroup('portal', navigationGroups.portal)}
            {renderNavGroup('transactional', navigationGroups.transactional)}
            {renderNavGroup('finance', navigationGroups.finance)}
          </nav>

          {/* Quick Actions - Role-based */}
          {(() => {
            const userRoles = user.roles || [user.role];
            
            // Define quick actions with their allowed roles
            const quickActions = [
              {
                id: 'new-booking',
                name: 'Booking',
                icon: Calendar,
                roles: ['hotel_admin', 'manager', 'front_office'],
                color: 'orange',
                onClick: () => openTab({ type: TAB_TYPES.NEW_BOOKING, id: `new-booking-${Date.now()}`, title: 'New Booking', data: {} })
              },
              {
                id: 'checkin',
                name: 'Check-in',
                icon: Users,
                roles: ['hotel_admin', 'manager', 'front_office'],
                color: 'green',
                onClick: () => openTab({ type: TAB_TYPES.PAGE_BOOKINGS, id: 'bookings-reserved', title: 'Bookings - Reserved', data: { defaultTab: 'reserved' } })
              },
              {
                id: 'restaurant-pos',
                name: 'POS',
                icon: Store,
                roles: ['hotel_admin', 'manager', 'restaurant_staff', 'service_manager'],
                color: 'blue',
                onClick: () => window.open('/restaurant', 'pos-popup', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no')
              }
            ];
            
            // Filter actions based on user roles
            const allowedActions = quickActions.filter(action => 
              action.roles.some(r => userRoles.includes(r))
            );
            
            // Don't render Quick Actions section if no actions allowed
            if (allowedActions.length === 0) return null;
            
            const colorClasses = {
              orange: 'bg-orange-50 hover:bg-orange-100 text-orange-700',
              green: 'bg-green-50 hover:bg-green-100 text-green-700',
              blue: 'bg-blue-50 hover:bg-blue-100 text-blue-700'
            };
            
            return (
              <div className="px-2 py-2 border-t border-gray-100">
                <p className="px-2 pb-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Quick Actions</p>
                <div className="flex gap-1">
                  {allowedActions.map(action => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.id}
                        onClick={action.onClick}
                        className={`flex-1 flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-md transition-colors ${colorClasses[action.color]}`}
                        data-testid={`quick-${action.id}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="text-[9px] font-medium">{action.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Powered by Bizionix */}
          <div className="px-3 py-2 border-t border-slate-700 bg-slate-800">
            <a 
              href="https://bizionix.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 text-[10px] text-white hover:text-orange-400 transition-colors"
            >
              <span className="text-gray-300">Powered by</span>
              <span className="font-semibold text-white">BIZIONIX</span>
            </a>
          </div>
        </div>
      </aside>

      {/* Main content area with TopHeader */}
      <div className="lg:pl-60 flex flex-col min-h-screen">
        {/* Top Header Strip */}
        <TopHeader
          user={user}
          onLogout={onLogout}
          selectedCompany={selectedCompany}
          onCompanyChange={onCompanyChange}
          activeHotel={activeHotel}
          onHotelChange={onHotelChange}
          selectedFinancialYear={selectedFinancialYear}
          onFinancialYearChange={onFinancialYearChange}
        />
        
        {/* Main content */}
        <main className="flex-1 p-4 lg:p-6 pt-4 bg-gray-50">
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Tabs Container - Shows when tabs are open */}
      <TabsContainer />
      
      {/* Mizu Dialer for admin users - floating phone icon */}
      {user?.hotel_id && ['hotel_admin', 'manager', 'reception'].includes(user?.role) && (
        <MizuDialer 
          hotelId={user.hotel_id} 
          isPortal={false}
          guestCredentials={{
            username: user.email?.split('@')[0] || 'admin',
            password: '', // Will be fetched from backend
            display_name: user.name || user.email
          }}
        />
      )}
    </div>
  );
}
